import React, { useEffect, useState, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import { Table } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import {
  useErrorService,
  useUserService,
  useNotificationService,
  FeatherLoader,
} from '@abroad/components'
import API from '../../utils/API'
// Guideline modals removed: coach schedules multiple sessions directly.
// import ScheduleGuidelineModal from './ScheduleGuidelineModal'
// import ConditionalScheduleGuidelineModal from './ConditionalScheduleGuidelineModal'
import AvailabilityRequiredModal, {
  SCHEDULING_REQUIREMENT_VARIANT,
} from './AvailabilityRequiredModal'
import { TAB_TYPES } from './SettingTab'
import { runSchedulingPreconditionChecks } from './schedulingPreconditions'
import CancelSessionModal from './CancelSessionModal'
import SessionSchedulingCalendar from './SessionSchedulingCalendar'
import ConditionalSessionSchedulingCalendar from './ConditionalSessionSchedulingCalendar'
import ConditionalRemainingSessionsSchedulingCalendar from './ConditionalRemainingSessionsSchedulingCalendar'
import SchedulingTabFilters from './SchedulingTabFilters'
import SchedulingListTitle from './components/SchedulingListTitle'

const ToBeScheduledTab = ({
  timezone,
  isCalendarConnected,
  onFilterRender,
  onScheduleSuccess,
}) => {
  const { user } = useUserService()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const errorServiceRef = useRef(Error)
  const history = useHistory()

  //   useEffect(() => {
  //     errorServiceRef.current = Error
  //   }, [Error])

  const [isLoading, setIsLoading] = useState(true)
  const [toBeScheduledSessions, setToBeScheduledSessions] = useState([])
  // const [showScheduleGuideline, setShowScheduleGuideline] = useState(false)
  const [schedulingRequirementModal, setSchedulingRequirementModal] =
    useState(null)
  const [selectedCoachRow, setSelectedCoachRow] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [sessionPendingCancel, setSessionPendingCancel] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showSchedulingCalendar, setShowSchedulingCalendar] = useState(false)
  const [
    showConditionalSchedulingCalendar,
    setShowConditionalSchedulingCalendar,
  ] = useState(false)
  const [
    showConditionalRemainingSchedulingCalendar,
    setShowConditionalRemainingSchedulingCalendar,
  ] = useState(false)
  const [schedulingType, setSchedulingType] = useState(null) // 'first-session' | 'remaining' | 'schedule-all'
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  // const [
  //   showConditionalScheduleGuideline,
  //   setShowConditionalScheduleGuideline,
  // ] = useState(false)
  const [selectedCoachRowForConditional, setSelectedCoachRowForConditional] =
    useState(null)
  const [hasMore, setHasMore] = useState(false)
  const currentPage = useRef(1)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(true)
  const [hasVideoCallLink, setHasVideoCallLink] = useState(true)
  const [filterUI, setFilterUI] = useState(null)
  const observer = useRef(null)
  const loader = useRef(null)
  const isNotProduction = process.env.REACT_APP_ENV !== 'prod'

  const coachId = user?._id

  const fetchSessions = useCallback(
    async (loadMore = false, page = 1) => {
      try {
        if (!coachId) {
          setIsLoading(false)
          return
        }

        if (!loadMore) {
          setToBeScheduledSessions([])
          setIsLoading(true)
          currentPage.current = 1
        } else {
          setIsLoadingMore(true)
        }

        const queryParams = {
          page,
          limit: 20,
        }
        if (selectedClientId) {
          queryParams.clientId = selectedClientId
        }

        const { data } =
          await API.AdminAPI.scheduling.getCoachToBeSchedulingList(queryParams)

        if (data?.success) {
          const newSessions = data?.data || []
          setIsScheduleEnabled(data.isScheduleEnabled !== false)
          setHasVideoCallLink(data.hasVideoCallLink !== false)
          if (loadMore) {
            setToBeScheduledSessions((prev) => [...prev, ...newSessions])
          } else {
            setToBeScheduledSessions(newSessions)
          }

          // Set pagination state from pagination object (if available)
          const pagination = data?.pagination || {}
          const apiTotalPages = pagination.totalPages || 0
          const apiCurrentPage = pagination.page || page
          currentPage.current = apiCurrentPage

          // If pagination object exists, use it; otherwise fallback to length check
          if (apiTotalPages > 0) {
            // hasMore is true if current page is less than total pages
            setHasMore(apiCurrentPage < apiTotalPages)
          } else {
            // Fallback: check if returned data length equals limit
            const hasMoreData = newSessions.length === 20
            setHasMore(hasMoreData)
          }
        } else {
          if (!loadMore) {
            setToBeScheduledSessions([])
          }
          setHasMore(false)
        }
      } catch (e) {
        errorServiceRef.current.showError(e)
        if (!loadMore) {
          setToBeScheduledSessions([])
        }
        setHasMore(false)
      } finally {
        if (loadMore) {
          setIsLoadingMore(false)
        } else {
          setIsLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coachId, selectedClientId],
  )

  useEffect(() => {
    fetchSessions(false, 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId])

  const handleClientChange = useCallback((clientId) => {
    setSelectedClientId(clientId)
  }, [])

  const handleFilterRender = useCallback(
    (ui) => {
      setFilterUI(ui)
      if (onFilterRender) {
        onFilterRender(ui)
      }
    },
    [onFilterRender],
  )

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoading && !isLoadingMore) {
      fetchSessions(true, currentPage.current + 1)
    }
  }, [hasMore, isLoading, isLoadingMore, fetchSessions])

  const handleObserver = useCallback(
    (entities) => {
      const target = entities[0]
      if (target.isIntersecting && hasMore && !isLoading && !isLoadingMore) {
        handleLoadMore()
      }
    },
    [hasMore, isLoading, isLoadingMore, handleLoadMore],
  )

  useEffect(() => {
    // Simple logic: If hasMore is true, set up observer to watch loader element
    if (hasMore && !isLoading && !isLoadingMore) {
      const options = {
        root: null,
        rootMargin: '20px',
        threshold: 1.0,
      }
      observer.current = new IntersectionObserver(handleObserver, options)
      if (loader.current && observer.current) {
        observer.current.observe(loader.current)
      }
    } else {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoading, isLoadingMore, handleObserver])

  // Check if should use new conditional flow
  const shouldUseConditionalFlow = (coachRow) => {
    const totalSessions = coachRow?.totalSessions || 0
    const isConditionalSchedulingEnabled =
      coachRow?.isConditionalSchedulingEnabled || false
    return totalSessions <= 3 && isConditionalSchedulingEnabled === true
  }

  // Check if should use old flow (unused now that guideline modals are removed)
  // const shouldUseOldFlow = (coachRow) => {
  //   const totalSessions = coachRow?.totalSessions || 0
  //   const isConditionalSchedulingEnabled =
  //     coachRow?.isConditionalSchedulingEnabled || false
  //   const sowType = coachRow?.sowType || coachRow?.sow?.sowType
  //   // Old flow: For QUEST/REGULAR types, if (totalSessions > 3) OR (totalSessions <= 3 && isConditionalSchedulingEnabled === false)
  //   if (sowType === 'QUEST' || sowType === 'REGULAR') {
  //     return (
  //       totalSessions > 3 ||
  //       (totalSessions <= 3 && isConditionalSchedulingEnabled === false)
  //     )
  //   }
  //   // For other types, use old flow by default if not conditional
  //   return !shouldUseConditionalFlow(coachRow)
  // }

  const openScheduleModal = (coachRow) => {
    // Guideline modals removed: open the calendar directly so the coach can
    // book multiple sessions at once.
    // if (shouldUseConditionalFlow(coachRow)) {
    //   setSelectedCoachRowForConditional(coachRow)
    //   setShowConditionalScheduleGuideline(true)
    // } else if (shouldUseOldFlow(coachRow)) {
    //   setSelectedCoachRow(coachRow)
    //   setShowScheduleGuideline(true)
    // }
    if (shouldUseConditionalFlow(coachRow)) {
      // New conditional flow
      setSelectedCoachRowForConditional(coachRow)
      setShowConditionalSchedulingCalendar(true)
    } else {
      // Old flow
      setSelectedCoachRow(coachRow)
      setSchedulingType('schedule-all')
      setShowSchedulingCalendar(true)
    }
  }

  // const closeScheduleModal = () => {
  //   setSelectedCoachRow(null)
  //   setShowScheduleGuideline(false)
  // }

  // const closeConditionalScheduleModal = () => {
  //   setSelectedCoachRowForConditional(null)
  //   setShowConditionalScheduleGuideline(false)
  // }

  // const handleConditionalScheduleAll = (coachRow) => {
  //   setSelectedCoachRowForConditional(coachRow)
  //   setShowConditionalScheduleGuideline(false)
  //   setShowConditionalSchedulingCalendar(true)
  // }

  const closeSchedulingRequirementModal = () => {
    setSchedulingRequirementModal(null)
    setSelectedCoachRow(null)
  }

  const handleSchedulingRequirementConfirm = () => {
    setSchedulingRequirementModal(null)
    setSelectedCoachRow(null)

    if (
      schedulingRequirementModal ===
      SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK
    ) {
      history.push('/coach/scheduling/zoom')
      return
    }

    history.push('/coach/scheduling/setting', {
      settingTab: TAB_TYPES.AVAILABILITY,
    })
  }

  const handleScheduleAction = (row) => {
    const canProceed = runSchedulingPreconditionChecks({
      hasVideoCallLink,
      isScheduleEnabled,
      onRequireVideoCallLink: () =>
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK,
        ),
      onRequireAvailability: () => {
        setSelectedCoachRow(row)
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.AVAILABILITY,
        )
      },
    })
    if (!canProceed) {
      return
    }

    const isFirstSession = row?.totalSessions === row?.remainingSessions
    if (isFirstSession) {
      openScheduleModal(row)
    } else {
      handleScheduleRemaining(row)
    }
  }

  // const handleScheduleFirstSession = () => {
  //   if (!selectedCoachRow) return
  //   setSchedulingType('first-session')
  //   setShowScheduleGuideline(false)
  //   setShowSchedulingCalendar(true)
  // }

  // const handleScheduleAll = () => {
  //   if (!selectedCoachRow) return
  //   setSchedulingType('schedule-all')
  //   setShowScheduleGuideline(false)
  //   setShowSchedulingCalendar(true)
  // }

  const handleScheduleRemaining = (coachRow) => {
    // Check if should use conditional flow for remaining sessions
    const totalSessions = coachRow?.totalSessions || 0
    const isConditionalSchedulingEnabled =
      coachRow?.isConditionalSchedulingEnabled || false

    if (totalSessions <= 3 && isConditionalSchedulingEnabled === true) {
      // Use conditional remaining sessions flow
      setSelectedCoachRowForConditional(coachRow)
      setShowConditionalRemainingSchedulingCalendar(true)
    } else {
      // Use old flow
      setSelectedCoachRow(coachRow)
      setSchedulingType('schedule-all') // Use schedule-all flow to enable recurring pattern selection
      setShowSchedulingCalendar(true)
    }
  }

  const handleScheduleSuccess = () => {
    setShowSchedulingCalendar(false)
    setShowConditionalSchedulingCalendar(false)
    setShowConditionalRemainingSchedulingCalendar(false)
    setSelectedCoachRow(null)
    setSelectedCoachRowForConditional(null)
    setSchedulingType(null)
    fetchSessions(false, 1)
    // Redirect to Scheduled tab after successful scheduling
    if (onScheduleSuccess) {
      onScheduleSuccess()
    }
  }

  const handleBackFromCalendar = () => {
    setShowSchedulingCalendar(false)
    setShowConditionalSchedulingCalendar(false)
    setShowConditionalRemainingSchedulingCalendar(false)
    setSelectedCoachRow(null)
    setSelectedCoachRowForConditional(null)
    setSchedulingType(null)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setSessionPendingCancel(null)
  }

  const handleCancelConfirm = async () => {
    if (!sessionPendingCancel) {
      return
    }

    const eventCoachId = sessionPendingCancel?.coachId
    const eventId = sessionPendingCancel?.eventId || sessionPendingCancel?._id

    if (!eventCoachId || !eventId) {
      Notification.showNotification(
        'Unable to find session details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsCancelling(true)
      const { data } = await API.ClientAPI.session.cancelSession(
        eventCoachId,
        eventId,
      )

      if (!data?.success) {
        Notification.showNotification(
          data?.message ||
            'Unable to cancel the session. Please try again later.',
          'danger',
        )
        return
      }

      Notification.showNotification(
        data?.message || 'Session cancelled successfully.',
        'success',
      )
      closeCancelModal()
      await fetchSessions(false, 1)
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCancelling(false)
    }
  }

  const getClientName = (row) => row?.clientName || '-'
  const getClientId = (row) => row?.clientId || '-'
  // const getSessionsCount = (row) => row?.remainingSessions || '-'

  const getTimeValue = (row) => {
    if (row?.timeLabel) {
      return row.timeLabel
    }
    if (row?.slotStart) {
      // Convert UTC timestamp to coach's selected timezone for display
      if (timezone) {
        const dateTime = moment.utc(row.slotStart).tz(timezone)
        const date = dateTime.format('MM/DD/YYYY')
        const time = dateTime.format('LT')
        return `${date} ${time}`
      } else {
        // Fallback to local timezone if timezone not provided
        const date = moment(row.slotStart).format('MM/DD/YYYY')
        const time = moment(row.slotStart).format('LT')
        return `${date} ${time}`
      }
    }
    if (row?.scheduledAt) {
      // Convert UTC timestamp to coach's selected timezone for display
      if (timezone) {
        const dateTime = moment.utc(row.scheduledAt).tz(timezone)
        const date = dateTime.format('MM/DD/YYYY')
        const time = dateTime.format('LT')
        return `${date} ${time}`
      } else {
        // Fallback to local timezone if timezone not provided
        const date = moment(row.scheduledAt).format('MM/DD/YYYY')
        const time = moment(row.scheduledAt).format('LT')
        return `${date} ${time}`
      }
    }
    return row?.time || '-'
  }
  // If showing conditional remaining scheduling calendar, render it
  if (
    showConditionalRemainingSchedulingCalendar &&
    selectedCoachRowForConditional
  ) {
    return (
      <ConditionalRemainingSessionsSchedulingCalendar
        coachDetails={selectedCoachRowForConditional}
        onScheduleSuccess={handleScheduleSuccess}
        onBack={handleBackFromCalendar}
        timezone={timezone}
      />
    )
  }

  // If showing conditional scheduling calendar, render it
  if (showConditionalSchedulingCalendar && selectedCoachRowForConditional) {
    return (
      <ConditionalSessionSchedulingCalendar
        coachDetails={selectedCoachRowForConditional}
        onScheduleSuccess={handleScheduleSuccess}
        onBack={handleBackFromCalendar}
        timezone={timezone}
      />
    )
  }

  // If showing scheduling calendar, render it
  if (showSchedulingCalendar && selectedCoachRow) {
    return (
      <SessionSchedulingCalendar
        schedulingType={schedulingType}
        coachDetails={selectedCoachRow}
        onScheduleSuccess={handleScheduleSuccess}
        onBack={handleBackFromCalendar}
        timezone={timezone}
      />
    )
  }

  return (
    <div>
      <SchedulingTabFilters
        selectedClientId={selectedClientId}
        onClientChange={handleClientChange}
        onFilterRender={handleFilterRender}
        isLoading={isLoading}
        disabled={showSchedulingCalendar}
      />
      <section className='mb-4'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <SchedulingListTitle
            title='To Be Scheduled Lists'
            showPreview={isCalendarConnected && !!timezone}
            timezone={timezone}
          />
          {filterUI?.clearFilter}
        </div>
        <div className='table-responsive'>
          <Table className='custom-table align-middle'>
            <thead>
              <tr className='s10a border-dark'>
                <th width={isNotProduction ? `20%` : `50%`}>CLIENT NAME</th>
                {isNotProduction && <th width='60%'>PLAN NAME</th>}
                <th
                  width={isNotProduction ? `20%` : `50%`}
                  className='text-center'>
                  ACTIONS
                </th>
              </tr>
            </thead>
            {isLoading && toBeScheduledSessions.length === 0 && (
              <tr>
                <td className='text-center border-0 pt-3' colSpan='2'>
                  <FeatherLoader />
                </td>
              </tr>
            )}
            {toBeScheduledSessions.length === 0 && !isLoading && (
              <tr>
                <td className='text-center s8 py-3' colSpan='2'>
                  No sessions to schedule.
                </td>
              </tr>
            )}
            <tbody>
              {toBeScheduledSessions.length > 0 &&
                !isLoading &&
                toBeScheduledSessions.map((row, index) => {
                  return (
                    <tr key={`${row?.sowId || row?.coachId}-${index}`}>
                      <td width={isNotProduction ? `20%` : `50%`}>
                        <span className='word-breakall'>
                          <NavLink
                            to={`/coach/clients/${getClientId(row)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-dark-900'>
                            {getClientName(row)}
                          </NavLink>
                        </span>
                      </td>
                      {isNotProduction && (
                        <td width='60%'>
                          <span className='word-breakall'>{row?.planName}</span>
                        </td>
                      )}
                      <td
                        width={isNotProduction ? `20%` : `50%`}
                        className='text-center'>
                        <div
                          className='text-saffron-700 hover cursor-pointer'
                          onClick={() => handleScheduleAction(row)}>
                          {row?.totalSessions !== row?.remainingSessions
                            ? 'Schedule More'
                            : 'Schedule'}
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
            <tfoot>
              <tr ref={loader}>
                <td colSpan='2' className='text-center py-2'>
                  {isLoadingMore && <FeatherLoader />}
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </section>
      {/* <ScheduleGuidelineModal
        show={showScheduleGuideline}
        handleClose={closeScheduleModal}
        coachDetails={selectedCoachRow}
        onScheduleFirstSession={handleScheduleFirstSession}
        onScheduleAll={handleScheduleAll}
      />
      <ConditionalScheduleGuidelineModal
        show={showConditionalScheduleGuideline}
        handleClose={closeConditionalScheduleModal}
        coachDetails={selectedCoachRowForConditional}
        onScheduleAll={handleConditionalScheduleAll}
      /> */}
      <AvailabilityRequiredModal
        show={Boolean(schedulingRequirementModal)}
        variant={schedulingRequirementModal}
        handleClose={closeSchedulingRequirementModal}
        onConfirm={handleSchedulingRequirementConfirm}
      />
      <CancelSessionModal
        show={showCancelModal}
        onClose={closeCancelModal}
        onConfirm={handleCancelConfirm}
        clientName={getClientName(sessionPendingCancel)}
        sessionTime={
          sessionPendingCancel ? getTimeValue(sessionPendingCancel) : ''
        }
        isSubmitting={isCancelling}
      />
    </div>
  )
}

export default ToBeScheduledTab
